// Body painter: draws one solved skeleton (torso, head, legs; never the arms)
// with the mech's part library, in a fixed back-to-front order:
//   floating rear parts → rear leg → backpack → pelvis → torso → halo → head
//   → front leg → front hip plate → floating front parts → shutdown sparks.
// The same routine runs in 'body' mode (visible sprite) and 'glow' mode (white
// emissive mask), so the glow sheet always lines up with the body sheet.

import { PART_LIBS, jagged, seeded, type PartLib, type Side } from './bodies';
import { Drawer, canvas, ctx2d, stampOutlined, SIL_INK, type Mode } from './draw';
import type { FrameFx } from './poses';
import { apply, type LegSolved, type Skeleton } from './rig';
import type { MechStyle, Pt } from './styles';

/** Depth (darkening) applied to the far leg so the two legs separate clearly at gameplay size. */
export const REAR_DEPTH = 0.7;

function paintLeg(d: Drawer, lib: PartLib, leg: LegSolved, fx: FrameFx, side: Side): void {
  // Foot first so the shin's ankle cuff overlaps it; the shin's knee cap overlaps the thigh.
  d.at(leg.foot, () => lib.foot(d, fx, side));
  d.at(leg.thigh, () => lib.thigh(d, fx, side));
  d.at(leg.shin, () => lib.shin(d, fx, side));
}

/** Shutdown sparks: short jagged discharges jumping off the joints (death sheet). */
function paintSparks(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void {
  if (fx.sparks <= 0.02) return;
  const rnd = seeded(fx.seed ^ 0x5bd1e995);
  const t = style.torso;
  const sources: Pt[] = [
    apply(sk.torso, t.chest),
    apply(sk.torso, t.shoulderF),
    apply(sk.torso, [t.back[0] * 0.6, t.back[1]]),
    sk.legF.knee,
    sk.legR.knee,
    apply(sk.head, style.headCenter),
  ];
  const n = Math.round(1 + fx.sparks * 4);
  for (let i = 0; i < n; i++) {
    const src = sources[Math.floor(rnd() * sources.length)];
    const ang = -Math.PI / 2 + (rnd() * 2 - 1) * 1.6;
    const len = 5 + rnd() * 8 * fx.sparks;
    const end: Pt = [src[0] + Math.cos(ang) * len, src[1] + Math.sin(ang) * len];
    d.energy(jagged(src, end, 3, 1.6, rnd), 0.8, 0.5 + 0.5 * fx.sparks, 0xfff1c4);
    // A hot ember at the tip.
    d.energy([end, [end[0] + 0.4, end[1] + 0.4]], 1.4, fx.sparks, 0xffffff);
  }
}

/** Paint the body (no arms) for one frame onto the drawer's context (cell coordinates). */
export function paintBody(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void {
  const lib = PART_LIBS[style.id];
  d.glowK = fx.glow;
  d.depth = 1;
  lib.behind?.(d, style, sk, fx);

  d.depth = REAR_DEPTH;
  paintLeg(d, lib, sk.legR, fx, 'R');
  d.depth = 1;

  d.at(sk.torso, () => lib.backpack(d, fx));
  d.at(sk.pelvis, () => lib.pelvis(d, fx));
  d.at(sk.torso, () => lib.torso(d, fx));
  lib.behindHead?.(d, style, sk, fx);
  d.at(sk.head, () => lib.head(d, fx));

  paintLeg(d, lib, sk.legF, fx, 'F');
  d.at(sk.pelvis, () => lib.hipPlate(d, fx));
  lib.front?.(d, style, sk, fx);
  paintSparks(d, style, sk, fx);
}

/** Reusable scratch canvases for one render scale. */
export class CellPainter {
  readonly scale: number;
  readonly size: number;
  private bodyScratch: HTMLCanvasElement;
  private glowScratch: HTMLCanvasElement;
  private sil: HTMLCanvasElement;

  /** @param cell logical cell size (128); @param scale render scale (1 = sheet, 4 = portrait). */
  constructor(cell: number, scale = 1) {
    this.scale = scale;
    this.size = Math.ceil(cell * scale);
    this.bodyScratch = canvas(this.size, this.size);
    this.glowScratch = canvas(this.size, this.size);
    this.sil = canvas(this.size, this.size);
  }

  private paint(target: HTMLCanvasElement, mode: Mode, style: MechStyle, sk: Skeleton, fx: FrameFx, ox: number, oy: number): void {
    const g = ctx2d(target);
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, target.width, target.height);
    g.setTransform(this.scale, 0, 0, this.scale, ox * this.scale, oy * this.scale);
    const d = new Drawer(g, mode, style.pal, this.scale);
    d.inkScale = this.scale > 1 ? 0.8 : 1;
    paintBody(d, style, sk, fx);
    g.setTransform(1, 0, 0, 1, 0, 0);
  }

  /**
   * Render one frame. The body is stamped with the silhouette outline into
   * `body` at (dx, dy); the white emissive mask is copied into `glow`.
   * (ox, oy) shifts the logical cell inside the scratch (for portraits).
   */
  render(
    style: MechStyle,
    sk: Skeleton,
    fx: FrameFx,
    body: CanvasRenderingContext2D,
    glow: CanvasRenderingContext2D | null,
    dx: number,
    dy: number,
    ox = 0,
    oy = 0,
  ): void {
    this.paint(this.bodyScratch, 'body', style, sk, fx, ox, oy);
    stampOutlined(body, dx, dy, this.bodyScratch, this.sil, SIL_INK * this.scale * (this.scale > 1 ? 0.8 : 1));
    if (glow) {
      this.paint(this.glowScratch, 'glow', style, sk, fx, ox, oy);
      glow.drawImage(this.glowScratch, dx, dy);
    }
  }
}
